import { useState, useEffect } from "react";
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from "@/components/ui/table";
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle 
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Loader2, Search, Calendar as CalendarIcon, Settings, PauseCircle, PlayCircle, ClipboardEdit } from "lucide-react";
import { format, addDays } from "date-fns";
import { db } from "../firebase/config";
import { collection, getDocs, doc, updateDoc, setDoc, getDoc, Timestamp } from "firebase/firestore";
import OwnerSidebar from "./OwnerSidebar";
import OwnerHeader from "./OwnerHeader";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { cn } from "@/lib/utils";
import { SidebarProvider } from "@/components/ui/sidebar";
import ConfirmDialog from "../components/ConfirmDialog";

interface SchoolSubscription {
  id: string;
  schoolName: string;
  schoolEmail: string;
  accountNumber: string;
  subscriptionStatus: 'active' | 'trial' | 'paused' | 'expired';
  subscriptionPlan: 'free' | 'basic' | 'premium';
  subscriptionExpiry: Timestamp | null;
}

const OwnerSubscriptions = () => {
  const [schools, setSchools] = useState<SchoolSubscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [selectedSchool, setSelectedSchool] = useState<SchoolSubscription | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  
  const { toast } = useToast();

  // Confirmation Dialog State
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [schoolToToggle, setSchoolToToggle] = useState<SchoolSubscription | null>(null);

  // Owner-driven school registration
  const [isRegisterDialogOpen, setIsRegisterDialogOpen] = useState(false);
  const [schoolToRegister, setSchoolToRegister] = useState<SchoolSubscription | null>(null);
  const [isRegistering, setIsRegistering] = useState(false);
  const [regForm, setRegForm] = useState({
    schoolName: "",
    schoolMotto: "",
    schoolEmail: "",
    schoolAddress: "",
    county: "",
    schoolSystem: "",
    schoolType: "",
    adminFirstName: "",
    adminLastName: "",
    adminPosition: "",
    adminPhone: "",
  });

  const kenyanCounties = [
    "Baringo","Bomet","Bungoma","Busia","Elgeyo-Marakwet","Embu","Garissa","Homa Bay","Isiolo","Kajiado",
    "Kakamega","Kericho","Kiambu","Kilifi","Kirinyaga","Kisii","Kisumu","Kitui","Kwale","Laikipia",
    "Lamu","Machakos","Makueni","Mandera","Marsabit","Meru","Migori","Mombasa","Murang'a","Nairobi",
    "Nakuru","Nandi","Narok","Nyamira","Nyandarua","Nyeri","Samburu","Siaya","Taita-Taveta","Tana River",
    "Tharaka-Nithi","Trans Nzoia","Turkana","Uasin Gishu","Vihiga","Wajir","West Pokot"
  ];

  const handleOpenRegisterDialog = (school: SchoolSubscription) => {
    setSchoolToRegister(school);
    setRegForm({
      schoolName: school.schoolName !== "Unknown School" ? school.schoolName : "",
      schoolMotto: "",
      schoolEmail: school.schoolEmail.replace(" (Admin)", ""),
      schoolAddress: "",
      county: "",
      schoolSystem: "",
      schoolType: "",
      adminFirstName: "",
      adminLastName: "",
      adminPosition: "",
      adminPhone: "",
    });
    setIsRegisterDialogOpen(true);
  };

  const handleOwnerRegisterSchool = async () => {
    if (!schoolToRegister) return;
    const { schoolName, schoolEmail, schoolAddress, county, schoolSystem, schoolType } = regForm;
    if (!schoolName || !schoolEmail || !schoolAddress || !county || !schoolSystem || !schoolType) {
      toast({ title: "Validation Error", description: "Please fill all required school fields.", variant: "destructive" });
      return;
    }
    setIsRegistering(true);
    try {
      const schoolId = schoolToRegister.id;
      const cleanName = schoolName.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
      const uniqueSuffix = schoolId.slice(-4).toUpperCase();
      const accountNumber = `${cleanName.substring(0, 8)}${uniqueSuffix}`;

      await setDoc(doc(db, "schools", schoolId, "schoolDetails", "main"), {
        name: schoolName,
        motto: regForm.schoolMotto || "",
        email: schoolEmail,
        address: schoolAddress,
        county,
        system: schoolSystem,
        type: schoolType,
        accountNumber,
        logoUrl: "",
        registeredByOwner: true,
        updatedAt: new Date(),
      }, { merge: true });

      toast({ title: "School Registered", description: `${schoolName} has been registered successfully.` });
      setIsRegisterDialogOpen(false);
      setSchoolToRegister(null);
      fetchSchools();
    } catch (err: any) {
      console.error("Owner registration error:", err);
      toast({ title: "Error", description: err.message || "Failed to register school.", variant: "destructive" });
    } finally {
      setIsRegistering(false);
    }
  };

  // Edit Form State
  const [editStatus, setEditStatus] = useState<string>("");
  const [editPlan, setEditPlan] = useState<string>("");
  const [editExpiry, setEditExpiry] = useState<Date | undefined>(undefined);

  useEffect(() => {
    fetchSchools();
  }, []);

  const fetchSchools = async () => {
    setLoading(true);
    try {
      const schoolsRef = collection(db, "schools");
      const snapshot = await getDocs(schoolsRef);
      
      const schoolsData: SchoolSubscription[] = [];
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        let mainData = null;
        try {
          const mainRef = doc(db, `schools/${docSnap.id}/schoolDetails/main`);
          const mainSnap = await getDoc(mainRef);
          if (mainSnap.exists()) {
            mainData = mainSnap.data();
          }
        } catch (e) {
          console.error("Error fetching main subcollection for school", docSnap.id);
        }
        
        // Use document ID directly as the account number if one isn't found
        const accountNumber = mainData?.accountNumber || docSnap.id;

        schoolsData.push({
          id: docSnap.id,
          schoolName: mainData?.name || "Unknown School",
          schoolEmail: mainData?.email || data.adminEmail || "No Email",
          accountNumber: accountNumber,
          subscriptionStatus: mainData?.subscriptionStatus || "none",
          subscriptionPlan: mainData?.subscriptionPlan || "free",
          subscriptionExpiry: mainData?.subscriptionExpiry || null,
        });
      }

      setSchools(schoolsData);
    } catch (error) {
      console.error("Error fetching schools:", error);
      toast({
        title: "Error",
        description: "Failed to load subscriptions data.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (school: SchoolSubscription) => {
    setSelectedSchool(school);
    setEditStatus(school.subscriptionStatus);
    setEditPlan(school.subscriptionPlan);
    setEditExpiry(school.subscriptionExpiry ? school.subscriptionExpiry.toDate() : undefined);
    setIsEditDialogOpen(true);
  };

  const handlePauseToggle = (school: SchoolSubscription) => {
    setSchoolToToggle(school);
    setIsConfirmDialogOpen(true);
  };

  const confirmToggleSubscription = async () => {
    if (!schoolToToggle) return;
    
    const school = schoolToToggle;
    let newStatus = 'active';
    let previousStatus = null;

    if (school.subscriptionStatus === 'paused') {
      // Resuming: Check if we stored a previous status
      try {
        const schoolRef = doc(db, `schools/${school.id}/schoolDetails/main`);
        const docSnap = await getDoc(schoolRef);
        if (docSnap.exists() && docSnap.data().previousStatus) {
           newStatus = docSnap.data().previousStatus;
        } else {
           // Fallback logic if no previous status stored
           const now = new Date();
           const expiry = school.subscriptionExpiry?.toDate();
           if (expiry && expiry > now) {
             // If expiry is in future, assume they were on trial or active. 
             // Defaulting to active is safest for access, but let's check plan.
             newStatus = 'active';
           }
        }
      } catch (e) {
        console.error("Error fetching previous status", e);
      }
    } else {
      // Pausing
      newStatus = 'paused';
      previousStatus = school.subscriptionStatus;
    }

    try {
      const schoolRef = doc(db, `schools/${school.id}/schoolDetails/main`);
      const updateData: any = { 
        subscriptionStatus: newStatus 
      };
      
      // Only save previous status if we are pausing
      if (newStatus === 'paused' && previousStatus) {
        updateData.previousStatus = previousStatus;
      }

      await updateDoc(schoolRef, updateData);
      
      toast({
        title: newStatus === 'paused' ? "Subscription Paused" : "Subscription Resumed",
        description: `${school.schoolName} is now ${newStatus}.`,
      });
      fetchSchools();
    } catch (error) {
      console.error("Error toggling pause:", error);
      toast({
        title: "Error",
        description: "Failed to update status.",
        variant: "destructive",
      });
    } finally {
      setIsConfirmDialogOpen(false);
      setSchoolToToggle(null);
    }
  };

  const handleSaveSubscription = async () => {
    if (!selectedSchool) return;

    try {
      const schoolRef = doc(db, `schools/${selectedSchool.id}/schoolDetails/main`);
      
      await updateDoc(schoolRef, {
        subscriptionStatus: editStatus,
        subscriptionPlan: editPlan,
        subscriptionExpiry: editExpiry ? Timestamp.fromDate(editExpiry) : null
      });

      toast({
        title: "Success",
        description: "Subscription updated successfully.",
      });

      setIsEditDialogOpen(false);
      fetchSchools();
    } catch (error) {
      console.error("Error updating subscription:", error);
      toast({
        title: "Error",
        description: "Failed to update subscription.",
        variant: "destructive",
      });
    }
  };

  const filteredSchools = schools.filter(school => {
    const matchesSearch = school.schoolName.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          school.accountNumber.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === "all" || school.subscriptionStatus === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusBadge = (status: string) => {
    // merry christmass
    switch (status) {
      case 'active': return <Badge className="bg-green-500">Active (Paid)</Badge>;
      case 'trial': return <Badge className="bg-blue-500">Trial</Badge>;
      case 'paused': return <Badge className="bg-yellow-500">Paused</Badge>;
      case 'expired': return <Badge className="bg-red-500">Expired (Unpaid)</Badge>;
      case 'none': return <Badge variant="secondary">No Subscription</Badge>;
      default: return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <SidebarProvider>
      <div className="flex h-screen bg-gray-100 dark:bg-gray-900 w-full">
        <OwnerSidebar activeSection="Subscriptions" onSectionChange={() => {}} userRole="owner" />
        <div className="flex-1 flex flex-col overflow-hidden">
          <OwnerHeader />
        <div className="flex-1 overflow-auto p-8">
          <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Subscriptions</h1>
              <p className="text-muted-foreground">Manage school subscriptions, trials, and access.</p>
            </div>
          </div>

          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>All Schools</CardTitle>
                <div className="flex gap-2">
                  <div className="relative w-64">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search schools..."
                      className="pl-8"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-[180px]">
                      <SelectValue placeholder="Filter by status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="active">Active (Paid)</SelectItem>
                      <SelectItem value="trial">Trial</SelectItem>
                      <SelectItem value="paused">Paused</SelectItem>
                      <SelectItem value="expired">Expired (Unpaid)</SelectItem>
                      <SelectItem value="none">No Subscription</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center p-8">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>School Name</TableHead>
                      <TableHead>Account No.</TableHead>
                      <TableHead>Plan</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Expiry Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                      {filteredSchools.map((school) => (
                        <TableRow key={school.id}>
                          <TableCell className="font-medium">{school.schoolName}</TableCell>
                          <TableCell>{school.accountNumber}</TableCell>
                          <TableCell className="capitalize">{school.subscriptionPlan}</TableCell>
                          <TableCell>{getStatusBadge(school.subscriptionStatus)}</TableCell>
                          <TableCell>
                            {school.subscriptionExpiry 
                              ? format(school.subscriptionExpiry.toDate(), "PPP") 
                              : "N/A"}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-2">
                              {(school.schoolName === 'Unknown School' || !school.schoolName) && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  title="Register school on behalf of admin"
                                  onClick={() => handleOpenRegisterDialog(school)}
                                >
                                  <ClipboardEdit className="h-4 w-4 text-blue-600" />
                                </Button>
                              )}
                              <Button 
                                variant="ghost" 
                                size="icon" 
                                title={school.subscriptionStatus === 'paused' ? "Resume Subscription" : "Pause Subscription"}
                                onClick={() => handlePauseToggle(school)}
                              >
                                {school.subscriptionStatus === 'paused' ? (
                                  <PlayCircle className="h-4 w-4 text-green-600" />
                                ) : (
                                  <PauseCircle className="h-4 w-4 text-yellow-600" />
                                )}
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleEditClick(school)}>
                                Edit
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    {filteredSchools.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                          No schools found matching your criteria.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Subscription</DialogTitle>
            <DialogDescription>
              Update subscription details for {selectedSchool?.schoolName}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="status" className="text-right">
                Status
              </Label>
              <Select 
                value={editStatus} 
                onValueChange={(value) => {
                  setEditStatus(value);
                  if (value === 'trial') {
                    const today = new Date();
                    const futureDate = addDays(today, 14);
                    setEditExpiry(futureDate);
                    toast({
                      title: "Trial Date Set",
                      description: `Expiry automatically set to 14 days from now.`
                    });
                  }
                }}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="paused">Paused</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="none">No Subscription</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label htmlFor="plan" className="text-right">
                Plan
              </Label>
              <Select value={editPlan} onValueChange={setEditPlan}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select plan" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="basic">Basic</SelectItem>
                  <SelectItem value="premium">Premium</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <Label className="text-right">Expiry</Label>
              <div className="col-span-3 flex flex-col gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className={cn("w-full justify-start text-left font-normal", !editExpiry && "text-muted-foreground")}>
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {editExpiry ? format(editExpiry, "PPP") : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar mode="single" selected={editExpiry} onSelect={setEditExpiry} initialFocus />
                  </PopoverContent>
                </Popover>
                <div className="flex gap-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs h-7"
                    onClick={() => setEditExpiry(addDays(new Date(), 7))}
                  >
                    +7 Days
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs h-7"
                    onClick={() => setEditExpiry(addDays(new Date(), 14))}
                  >
                    +14 Days
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="text-xs h-7"
                    onClick={() => setEditExpiry(addDays(new Date(), 30))}
                  >
                    +30 Days
                  </Button>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button type="submit" onClick={handleSaveSubscription}>Save changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Owner-driven School Registration Dialog */}
      <Dialog open={isRegisterDialogOpen} onOpenChange={setIsRegisterDialogOpen}>
        <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ClipboardEdit className="h-5 w-5 text-blue-600" />
              Register School on Behalf of Admin
            </DialogTitle>
            <DialogDescription>
              Registering for school ID: <span className="font-mono font-semibold">{schoolToRegister?.id}</span>
              {schoolToRegister?.schoolEmail && (
                <span className="block mt-1 text-xs text-muted-foreground">Admin email: {schoolToRegister.schoolEmail}</span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-2">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground border-b pb-1">School Details</p>

            <div className="grid grid-cols-4 items-center gap-3">
              <Label className="text-right text-sm">School Name <span className="text-red-500">*</span></Label>
              <Input
                className="col-span-3"
                placeholder="e.g. Nairobi Academy"
                value={regForm.schoolName}
                onChange={e => setRegForm(p => ({ ...p, schoolName: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-3">
              <Label className="text-right text-sm">Motto</Label>
              <Input
                className="col-span-3"
                placeholder="Optional school motto"
                value={regForm.schoolMotto}
                onChange={e => setRegForm(p => ({ ...p, schoolMotto: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-3">
              <Label className="text-right text-sm">School Email <span className="text-red-500">*</span></Label>
              <Input
                className="col-span-3"
                type="email"
                placeholder="school@example.com"
                value={regForm.schoolEmail}
                onChange={e => setRegForm(p => ({ ...p, schoolEmail: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-3">
              <Label className="text-right text-sm">Address <span className="text-red-500">*</span></Label>
              <Input
                className="col-span-3"
                placeholder="Full school address"
                value={regForm.schoolAddress}
                onChange={e => setRegForm(p => ({ ...p, schoolAddress: e.target.value }))}
              />
            </div>

            <div className="grid grid-cols-4 items-center gap-3">
              <Label className="text-right text-sm">County <span className="text-red-500">*</span></Label>
              <Select value={regForm.county} onValueChange={v => setRegForm(p => ({ ...p, county: v }))}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select county" />
                </SelectTrigger>
                <SelectContent>
                  {kenyanCounties.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 items-center gap-3">
              <Label className="text-right text-sm">System <span className="text-red-500">*</span></Label>
              <Select value={regForm.schoolSystem} onValueChange={v => setRegForm(p => ({ ...p, schoolSystem: v }))}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select curriculum" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="CBC">CBC (Competency Based Curriculum)</SelectItem>
                  <SelectItem value="8-4-4">8-4-4 System</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-4 items-center gap-3">
              <Label className="text-right text-sm">School Type <span className="text-red-500">*</span></Label>
              <Select value={regForm.schoolType} onValueChange={v => setRegForm(p => ({ ...p, schoolType: v }))}>
                <SelectTrigger className="col-span-3">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Boarding School">Boarding School</SelectItem>
                  <SelectItem value="Day School">Day School</SelectItem>
                  <SelectItem value="Mixed (Day & Boarding)">Mixed (Day &amp; Boarding)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button variant="outline" onClick={() => setIsRegisterDialogOpen(false)} disabled={isRegistering}>
              Cancel
            </Button>
            <Button onClick={handleOwnerRegisterSchool} disabled={isRegistering} className="bg-blue-600 hover:bg-blue-700 text-white">
              {isRegistering ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Registering...</> : "Register School"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={isConfirmDialogOpen}
        title={schoolToToggle?.subscriptionStatus === 'paused' ? "Resume Subscription?" : "Pause Subscription?"}
        description={schoolToToggle?.subscriptionStatus === 'paused' 
          ? `Are you sure you want to resume access for ${schoolToToggle?.schoolName}? They will regain access immediately.`
          : `Are you sure you want to pause access for ${schoolToToggle?.schoolName}? All users from this school will be blocked immediately.`
        }
        confirmText={schoolToToggle?.subscriptionStatus === 'paused' ? "Resume Access" : "Pause Access"}
        cancelText="Cancel"
        onConfirm={confirmToggleSubscription}
        onCancel={() => setIsConfirmDialogOpen(false)}
      />
        </div>
      </div>
    </SidebarProvider>
  );
};

export default OwnerSubscriptions;
